import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui'
import type {
  ButtonVariant,
} from '@/components/ui/Button/Button'
import type { FormulationStatus } from '../api/types'

/**
 * Confirm dialog for a formulation status transition (Sprint 08b —
 * Item 5). Renders inline-modal overlay (Drawer > inline > modal per
 * `ops/CLAUDE.md`, but for a quick yes/no with a comment field, modal
 * is acceptable). Comment is optional per D-8.
 *
 * Caller passes `target` (the status we'd transition to) + a label;
 * dialog returns `{ comment }` on confirm, or fires onCancel.
 */

export interface TransitionDialogProps {
  open: boolean
  target: FormulationStatus
  label: string
  confirmVariant?: ButtonVariant
  onConfirm: (comment: string | null) => void
  onCancel: () => void
  pending?: boolean
}

export function TransitionDialog({
  open,
  target,
  label,
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  pending,
}: TransitionDialogProps) {
  const [comment, setComment] = useState('')

  // Reset the comment when the dialog re-opens for a new transition.
  // External-state sync (parent's open + target) — react-query / parent
  // dialogs follow the same pattern in this codebase.
  useEffect(() => {
    if (!open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setComment('')
  }, [open, target])

  if (!open) return null

  const targetLabel = target.charAt(0).toUpperCase() + target.slice(1)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="transition-dialog-title"
      className="fixed inset-0 z-modal flex items-center justify-center bg-overlay"
      onClick={onCancel}
    >
      <div
        className="flex w-full max-w-md flex-col gap-3 rounded-md border border-border-subtle bg-surface p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <h2
            id="transition-dialog-title"
            className="text-role-data-primary"
          >
            {label}
          </h2>
          <button
            type="button"
            aria-label="Cancel"
            onClick={onCancel}
            disabled={pending}
            className="cursor-pointer rounded-xs border-none bg-transparent p-0.5 text-tertiary hover:text-primary disabled:opacity-50"
          >
            <X size={14} />
          </button>
        </div>
        <p className="text-sm text-secondary">
          Transition to <strong>{targetLabel}</strong>. Add an optional
          comment for the history log:
        </p>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          autoFocus
          placeholder="e.g. ready for QC, batch 5 confirmed, etc."
          className="w-full border border-border-subtle bg-surface p-2 text-sm outline-none focus:border-accent"
        />
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onCancel}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            size="sm"
            onClick={() => onConfirm(comment.trim() || null)}
            loading={pending}
          >
            {label}
          </Button>
        </div>
      </div>
    </div>
  )
}
